import { Component, DestroyRef, inject, OnDestroy, OnInit, effect, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router'
import { filter } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NotificationService } from '../../services'
import { CoreService } from '../../services'
import { Order, Address } from 'depoto-core/src/entities'
import { SchemaUtil, historyBack, getObjectDiff, prepareOrderAddressForSave } from '../../utils'
import { BaseDetail } from '../_base-detail/base-detail'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { OrderDetailService } from '../../services/order-detail.service'
import { TranslateService } from '@ngx-translate/core'
import { FmtDatePipe } from '../../pipes'
import { AddressService } from '../../services/entity-services/address.service'
import { OrderService } from '../../services/entity-services/order.service'
import { updateCachedListQueries } from '../../services/query-cache.service'
import { injectQueryClient } from '@tanstack/angular-query-experimental'

const addressSchema = {
  city: null,
  companyName: null,
  country: null,
  dic: null,
  email: null,
  externalId: null,
  firstName: null,
  ic: null,
  id: null,
  isBilling: null,
  isStored: null,
  lastName: null,
  note: null,
  phone: null,
  state: null,
  street: null,
  branchId: null,
  zip: null,
}

@Component({
  selector: 'app-order',
  templateUrl: './order.component.html',
  styleUrls: ['order.component.scss'],
  providers: [FmtDatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OrderComponent extends BaseDetail implements OnInit, OnDestroy, CheckDataChanges {
  private queryClient = injectQueryClient()
  private readonly destroyRef = inject(DestroyRef)
  addressService = inject(AddressService)
  orderService = inject(OrderService)
  address: Address
  pristineOrder: Order
  private isDestroyed = false

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public notificationService: NotificationService,
    public translateService: TranslateService,
    public core: CoreService,
    public orderDetailService: OrderDetailService,
    private readonly fmtDatePipe: FmtDatePipe
  ) {
    super(route, router, core, translateService)
    effect(() => {
      this.orderDetailService.queryCurrencies.refetch()
      this.orderDetailService.queryDepotsOptions.refetch()
      this.orderDetailService.queryCompanyCarriers.refetch()
      this.orderDetailService.queryPayments.refetch
      this.orderDetailService.queryCheckouts.refetch()

      this.orderDetailService.querySuppliers.refetch()
      this.orderDetailService.queryVats.refetch()
      this.orderDetailService.queryTags.refetch()
    })
  }

  async ngOnInit() {
    // Keep activeLink (which drives the shared continue/back button's label and behaviour)
    // in sync with whichever wizard step is actually being shown. Without this, activeLink is
    // only ever updated by the specific method that triggered a step change (triggerNextStep,
    // triggerBackBtn, CustomerSearchComponent.selectCustomer); any navigation that bypasses those
    // (e.g. browser back/forward) leaves it stale, and the continue button ends up acting on the
    // wrong step - e.g. re-navigating to the address step you're already on instead of advancing.
    this.router.events
      .pipe(
        filter(val => val instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.orderDetailService.isCreating) {
          this.orderDetailService.activeLink = this.resolveActiveLinkFromUrl()
        }
      })

    this.orderDetailService.loading = true

    this.watchAccountSwitch(() => {
      if (!this.orderDetailService.isCreating && this.orderDetailService.order?.id && !this.hasUnsavedDataChanges()) {
        this.orderDetailService.refreshData(this.orderDetailService.order.id)
      }
    })

    // todo
    // this.orderDetailService.getCoreEntityLists()
    await this.reloadViewDataFromRoute()
    // If this instance was destroyed while the fetch above was in flight (navigated away before
    // it resolved), or the load failed and onLoadError() already redirected elsewhere, bail out
    // instead of touching orderDetailService.order/whatever page is now current.
    if (this.isDestroyed || !this.orderDetailService.order) return

    const id = this.route.snapshot.params['id']
    if (id === 0 || isNaN(id)) {
      this.orderDetailService.canCreateOrEdit = this.orderDetailService.canCreateOrEditNotes =
        this.core.services.user.hasRole('ROLE_ORDER_CREATE') || // TODO CORE USER
        this.core.services.user.hasRole('ROLE_SUPER_ADMIN') || // TODO CORE USER
        this.core.services.user.hasRole('ROLE_ADMIN') // TODO CORE USER

      this.orderDetailService.activeLink = 'customer'
      // "Create new order" already links straight to /order/customer, so we're normally already
      // here - re-navigating to the same URL from inside this route's own activation (ngOnInit)
      // is a needless re-entrant navigation and was observed to tear this component instance back
      // down mid-init. Only take the redirect when we actually landed somewhere else (bare
      // /order, /order/0, or a direct refresh on /order/address|/payment-info).
      if (this.router.url !== '/order/customer') {
        await this.router.navigate(['/order/customer'])
        // Same as above: this instance may have been torn down while that navigation was pending.
        if (this.isDestroyed || !this.orderDetailService.order) return
      }
    } else {
      this.orderDetailService.activeLink = ''
    }
    this.orderDetailService.canEditPrice =
      !this.orderDetailService.isCancelled &&
      (this.core.services.user.hasRole('ROLE_ORDER_ITEM_PRICE_UPDATE') || // TODO CORE USER
        this.core.services.user.hasRole('ROLE_SUPER_ADMIN') || // TODO CORE USER
        this.core.services.user.hasRole('ROLE_ADMIN')) // TODO CORE USER

    // todo check
    // let's correct pollibly wrong address data
    if (this.order?.shippingAddress?.country !== 'US' && this.order?.shippingAddress?.country !== 'CA') {
      if (this.orderDetailService.order?.shippingAddress) {
        this.orderDetailService.order.shippingAddress.state = ''
      } else {
        this.orderDetailService.order.shippingAddress = new Address()
      }
    }
    // let's correct long dates data (forms are unable to save seconds)
    this.order.dateExpedition = this.fmtDatePipe.transform(this.order.dateExpedition, 'yyyy-MM-ddTHH:mm')
    this.order.dateTax = this.fmtDatePipe.transform(this.order.dateTax, 'yyyy-MM-dd')
    this.order.dateDue = this.fmtDatePipe.transform(this.order.dateDue, 'yyyy-MM-dd')

    this.pristineOrder = structuredClone(this.orderDetailService.order)
  }

  ngOnDestroy(): void {
    this.unwatchAccountSwitch()
    this.isDestroyed = true
    this.orderDetailService.isCreating = false
    this.orderDetailService.activeLink = ''
    this.orderDetailService.order = undefined
    this.orderDetailService.updateProcessStatus = undefined
  }

  private resolveActiveLinkFromUrl(): string {
    const step = this.router.url.split('/')[2]
    return step === 'customer' || step === 'address' || step === 'payment-info' ? step : ''
  }

  hasUnsavedDataChanges(): boolean {
    // const diff = getObjectDiff(structuredClone(this.orderDetailService.order), this.orderDetailService.pristineOrder) // todo vypisuje ty props
    const diff = getObjectDiff(structuredClone(this.orderDetailService.order), this.pristineOrder)
    if (!diff) {
      return
    }
    return !!diff.filter(d => d !== 'files').length
  }

  private async reloadViewDataFromRoute() {
    this.orderDetailService.resetAddressStoreFlags()
    const id = Number(this.router.url.split('/')[2])
    if (!id || isNaN(id)) {
      this.orderDetailService.isCreating = true
      this.orderDetailService.activeLink = 'customer'
      this.orderDetailService.order = new Order({
        enabled: true,
        invoiceAddress: new Address(),
        shippingAddress: new Address(),
        currency: this.orderDetailService.queryCurrencies.data()?.find(c => c.id === 'CZK'),
      })
      this.orderDetailService.loading = false
      return
    }
    if (this.route.snapshot.routeConfig.path.includes('product-variant-create') && !isNaN(id) && id) {
      this.orderDetailService.isCreating = true
      this.orderDetailService.activeLink = ''
      this.orderDetailService.updateProcessStatus = undefined
      this.orderDetailService.order = new Order({ enabled: true })
      await this.orderDetailService.refreshData()
      this.pristineOrder = structuredClone(this.orderDetailService.order)
    } else {
      this.orderDetailService.isCreating = false
      this.orderDetailService.activeLink = ''
      this.orderDetailService.updateProcessStatus = undefined
      await this.orderDetailService.refreshData(id)
      this.pristineOrder = structuredClone(this.orderDetailService.order)
    }
  }

  public async confirmOrder() {
    if (this.hasUnsavedDataChanges()) {
      if (!confirm(this.translateService.instant('unsaved-changes.confirmation'))) return
    }
    if (!this.order || (this.order && !this.order.checkout)) {
      alert(this.translateService.instant('order.is.not.assigned.to.checkout'))
      return
    }
    if (
      !confirm(this.translateService.instant('order.reservation.confirmation', { id: this.order.reservationNumber }))
    ) {
      return
    }
    try {
      // TODO CORE MUTATION
      const result = await this.core.services.order.updateOrderPart({
        id: this.orderDetailService.order.id,
        status: {
          id: 'bill',
          name: 'Doklad',
        },
      })
      if (+result?.id) {
        // Update detail cache for this customer
        this.queryClient.setQueryData(['order', Number(result.id)], result)
        // Update all cached list queries beginning with ['customers', ...] so listings reflect the update immediately
        updateCachedListQueries(this.queryClient, ['orders'], result)

        this.notificationService.success(this.translateService.instant('alert.order.closed'))
        this.orderDetailService.isEditedFiles = false
        this.orderDetailService.isEditedAddress = false
        this.orderDetailService.canCreateOrEdit = false
        await this.orderDetailService.refreshData(this.order.id)
        this.pristineOrder = structuredClone(this.orderDetailService.order)
        return this.router.navigate(['/order/', this.order.id])
      }
    } catch (err) {
      console.error(err)
    }
  }

  public async deleteReservation() {
    if (
      !confirm(
        this.translateService.instant('order.reservation.delete-confirmation', { id: this.order.reservationNumber })
      )
    ) {
      return
    }
    try {
      // TODO CORE MUTATION
      await this.core.services.order.deleteReservation(this.order).then(() => {
        const idStr = String(this.route.snapshot.params['id'])
        updateCachedListQueries(this.queryClient, ['orders'], { id: Number(idStr) } as any, { mode: 'delete' })
        // Remove detail cache for this customer
        this.queryClient.removeQueries({ queryKey: ['order', Number(idStr)], exact: true })
        this.notificationService.success(this.translateService.instant('alert.reservation.deleted'))
        this.router.navigate(['/orders'], { replaceUrl: true })
      })
    } catch (err) {
      console.error(err)
    }
  }

  public async cancelReservation() {
    if (this.hasUnsavedDataChanges()) {
      if (!confirm(this.translateService.instant('unsaved-changes.confirmation'))) return
    }
    if (
      !confirm(
        this.translateService.instant('order.reservation.cancel-confirmation', { id: this.order.reservationNumber })
      )
    ) {
      return
    }
    this.orderDetailService.loading = true
    try {
      const errors = await this.orderService.cancelReservation(this.order)
      if (!errors?.length) {
        await this.orderDetailService.refreshData(this.order.id)
        this.orderDetailService.canCreateOrEdit = false
        this.pristineOrder = structuredClone(this.orderDetailService.order)
        // Update detail cache and cached list queries so listings reflect the new status immediately
        this.queryClient.setQueryData(['order', Number(this.order.id)], this.order)
        updateCachedListQueries(this.queryClient, ['orders'], this.order)
        this.notificationService.success(this.translateService.instant('alert.reservation.canceled'))
      }
    } catch (err) {
      console.error(err)
    } finally {
      this.orderDetailService.loading = false
    }
  }

  public downloadInvoiceUrl(): void {
    this.core.services.download.downloadDocumentAndShowInNewWindow(this.order.invoiceUrl)
  }

  public downloadInvoiceIsdocUrl(): void {
    this.core.services.download.downloadDocumentAndShowInNewWindow(this.order.invoiceIsdocUrl)
  }

  downloadPdf(movePack): void {
    this.core.services.download.downloadDocumentAndShowInNewWindow(movePack.pdfWOPricesUrl)
  }

  movePacksOut() {
    return this.orderDetailService.order.movePacks.filter(mP => mP.type?.id === 'out')
  }

  movePacksReservation() {
    return this.orderDetailService.order.movePacks.filter(mP => mP.type?.id === 'reservation')
  }

  public downloadReservationUrl(): void {
    this.core.services.download.downloadDocumentAndShowInNewWindow(this.order.reservationUrl)
  }

  private hasPaymentMethod(): boolean {
    return this.order.paymentItems && this.order.paymentItems.length > 0
  }

  private hasIncorrectPaymentMethod(): boolean {
    return (
      this.order.paymentItems &&
      this.order.paymentItems.length > 0 &&
      (!this.order.paymentItems[0].payment ||
        (this.order.paymentItems[0].payment && !this.order.paymentItems[0].payment.id))
    )
  }

  private hasAllNecessaryProps(): boolean {
    if (
      !this.orderDetailService.queryCompanyCarriers.data() ||
      (this.orderDetailService.queryCompanyCarriers.data() &&
        this.orderDetailService.queryCompanyCarriers.data().length === 0)
    ) {
      alert(this.translateService.instant('order.activate.at.least.one.carrier'))
      return false
    }
    if (!this.order.checkout.id) {
      alert(this.translateService.instant('order.activate.at.least.one.carrier'))

      return false
    }
    let overridePaymentItemsCheck = false
    if (!this.hasPaymentMethod()) {
      overridePaymentItemsCheck = confirm(this.translateService.instant('order.without.payments.confirmation'))
      if (!overridePaymentItemsCheck) {
        return false
      }
    }
    if (!overridePaymentItemsCheck && this.hasIncorrectPaymentMethod()) {
      alert(this.translateService.instant('order.select.payment-method'))
      return false
    }
    if (!this.order.carrier?.id) {
      alert(this.translateService.instant('order.select.carrier'))
      return false
    }
    return true
  }

  public async createOrder() {
    this.orderDetailService.loading = true
    if (this.orderDetailService.orderForm.invalid) {
      this.orderDetailService.loading = false
      return
    }
    if (
      this.orderDetailService.order.paymentItems?.length &&
      this.orderDetailService.order.paymentItems.some(pI => !pI.amount && pI.amount !== 0)
    ) {
      alert(this.translateService.instant('order.select.price'))
      this.orderDetailService.loading = false
      return
    }
    if (!this.hasAllNecessaryProps()) {
      this.orderDetailService.loading = false
      return
    }
    if (this.shouldPersistAddresses()) {
      await this.updateAddress()
    }
    // if (this.order.invoiceAddress?.country) {
    //   this.orderDetailService.order.invoiceAddress.id = await this.addressService
    //     .createAddress(
    //       { ...this.order.invoiceAddress, isBilling: true, isStored: false },
    //       this.order.customer ? (this.order.customer as any) : null,
    //       addressSchema
    //     )
    //     .then(a => a.id)
    //   if (this.orderDetailService.isShippingAddressSameAsInvoice) {
    //     this.orderDetailService.order.shippingAddress = this.order.invoiceAddress
    //   }
    // }
    // if (this.order.shippingAddress?.country && !this.orderDetailService.isShippingAddressSameAsInvoice) {
    //   this.orderDetailService.order.shippingAddress.id = await this.addressService
    //     .createAddress(
    //       { ...this.order.shippingAddress, isBilling: false, isStored: false },
    //       this.order.customer ? (this.order.customer as any) : null,
    //       addressSchema
    //     )
    //     .then(a => a.id)
    // }

    try {
      this.orderDetailService.getFormValue()
      // todo: this is hotfix to prevent duplicate reservationNumbers. resolve if we need to post this in core.order.create() ...
      if (this.order.reservationNumber > 0) {
        console.log('ERROR order.reservationNumber present on create: ', this.order.reservationNumber)
        this.order.reservationNumber = 0
      }
      const result = await this.core.services.order.create(this.order, SchemaUtil.order) // TODO CORE MUTATION
      if (+result?.id) {
        // Update detail cache for this customer
        this.queryClient.setQueryData(['order', Number(result.id)], result)
        // Update all cached list queries beginning with ['customers', ...] so listings reflect the change immediately
        updateCachedListQueries(this.queryClient, ['orders'], result, { mode: 'insert', insertPosition: 'start' })
        this.removeOrderListings()

        this.notificationService.success(this.translateService.instant('alert.order.created'))
        this.orderDetailService.order.id = result.id
        try {
          if (this.order.files?.length) {
            await this.connectUploadedFilesToOrder()
            await this.reloadViewDataFromRoute()
          }
        } catch (e) {
          this.notificationService.error(
            this.translateService.instant('alert.order.files-not-connected') + '\n ' + e.message
          )
        }
        this.orderDetailService.order = new Order(result)
      }
      this.pristineOrder = structuredClone(this.orderDetailService.order)
    } finally {
      this.orderDetailService.loading = false
      this.orderDetailService.activeLink = ''
      await this.router.navigate(['/order', this.order.id], { replaceUrl: true })
    }
  }

  public async updateOrder() {
    if (this.orderDetailService.isCancelled) {
      return
    }
    this.orderDetailService.loading = true
    if (this.orderDetailService.orderForm.invalid) {
      this.orderDetailService.loading = false
      return
    }
    if (
      this.orderDetailService.order.paymentItems?.length &&
      this.orderDetailService.order.paymentItems.some(pI => !pI.amount && pI.amount !== 0)
    ) {
      alert(this.translateService.instant('order.select.price'))
      this.orderDetailService.loading = false
      return
    }
    if (this.orderDetailService.isEditedFiles) {
      await this.connectUploadedFilesToOrder()
    }
    if (this.shouldPersistAddresses()) {
      await this.updateAddress()
    }
    try {
      const keepReservation = this.orderDetailService.order.status.id === 'reservation'
      this.orderDetailService.getFormValue()

      // TODO CORE MUTATION
      const result = await this.core.services.order.update(
        this.order,
        keepReservation,
        undefined,
        this.orderDetailService.updateProcessStatus,
        SchemaUtil.order
      )
      this.orderDetailService.canCreateOrEdit = !!keepReservation
      this.orderDetailService.isEditedFiles = false
      this.orderDetailService.isEditedTags = false
      this.orderDetailService.isEditedAddress = false
      if (+result?.id) {
        // Update detail cache for this customer
        this.queryClient.setQueryData(['order', Number(result.id)], result)
        // Update all cached list queries beginning with ['customers', ...] so listings reflect the update immediately
        updateCachedListQueries(this.queryClient, ['orders'], result)

        await this.orderDetailService.refreshData(this.order.id)
        this.orderDetailService.sortOrderItems()
        // this.orderDetailService.pristineOrder = structuredClone(this.orderDetailService.order)
        this.pristineOrder = structuredClone(this.orderDetailService.order)
        this.notificationService.success(this.translateService.instant('alert.order.saved'))
      }
    } catch (err) {
      console.error(err)
    } finally {
      this.orderDetailService.loading = false
    }
  }

  private async updateAddress() {
    const invoiceAddress = await this.persistOrderAddress(
      this.order.invoiceAddress,
      true,
      this.shouldStoreAddressOnCustomer(true)
    )
    if (invoiceAddress) {
      this.orderDetailService.order.invoiceAddress = invoiceAddress
    }

    if (this.orderDetailService.isShippingAddressSameAsInvoice) {
      this.orderDetailService.order.shippingAddress = invoiceAddress ?? this.order.invoiceAddress
    } else {
      this.orderDetailService.order.shippingAddress =
        (await this.persistOrderAddress(this.order.shippingAddress, false, this.shouldStoreAddressOnCustomer(false))) ??
        null
    }
  }

  private async persistOrderAddress(
    address: Address | Partial<Address> | null | undefined,
    isBilling: boolean,
    storeOnCustomer = false
  ): Promise<Address | null> {
    if (!address) {
      return null
    }

    const addressForSave = prepareOrderAddressForSave(address, isBilling, { storeOnCustomer })
    if (addressForSave.id > 0) {
      // TODO CORE MUTATION
      await this.core.services.address.update(addressForSave, this.order.customer ? (this.order.customer as any) : null)
      return addressForSave
    }
    if (!addressForSave.country) {
      return null
    }

    // TODO CORE MUTATION
    const createdAddress = await this.core.services.address.create(
      addressForSave,
      this.order.customer ? (this.order.customer as any) : null,
      addressSchema
    )

    return new Address({ ...addressForSave, id: createdAddress.id })
  }

  private shouldStoreAddressOnCustomer(isBilling: boolean): boolean {
    if (!this.orderDetailService.isCreating || !this.order.customer?.id) {
      return false
    }

    return isBilling
      ? this.orderDetailService.storeInvoiceAddressOnCustomer
      : this.orderDetailService.storeShippingAddressOnCustomer
  }

  private shouldPersistAddresses(): boolean {
    return (
      this.orderDetailService.isEditedAddress ||
      this.shouldCreateOrderAddressCopy(this.order.invoiceAddress) ||
      (!this.orderDetailService.isShippingAddressSameAsInvoice &&
        this.shouldCreateOrderAddressCopy(this.order.shippingAddress))
    )
  }

  private shouldCreateOrderAddressCopy(address: Address | Partial<Address> | null | undefined): boolean {
    if (!address?.country) {
      return false
    }

    return !address.id || address.isStored === true
  }

  private async connectUploadedFilesToOrder() {
    if (!this.order?.files?.length) {
      return
    }
    try {
      const promises = this.order.files.map(file => this.core.services.file.update(file, null, this.order.id)) // TODO CORE MUTATION
      await Promise.all(promises)
      this.orderDetailService.isEditedFiles = false
    } catch (error) {
      console.error('Error in function connectUploadedFilesToOrder. Details: ', error.message)
    }
  }

  public triggerBackBtn() {
    if (this.orderDetailService.isCreating) {
      if (this.orderDetailService.activeLink === 'customer') {
        this.orderDetailService.activeLink = ''
        this.router.navigate(['/orders'])
      } else if (this.orderDetailService.activeLink === 'address') {
        this.orderDetailService.activeLink = 'customer'
        this.router.navigate(['/order/customer'])
      } else if (this.orderDetailService.activeLink === 'payment-info') {
        this.orderDetailService.activeLink = 'address'
        this.router.navigate(['/order/address'])
      } else if (this.orderDetailService.activeLink === '') {
        this.orderDetailService.activeLink = 'payment-info'
        this.router.navigate(['/order/payment-info'])
      }
    } else return historyBack()
  }

  public async triggerNextStep() {
    if (this.orderDetailService.activeLink === 'customer') {
      this.orderDetailService.activeLink = 'address'
      await this.router.navigate(['/order/address'], { replaceUrl: true })
    } else if (this.orderDetailService.activeLink === 'address') {
      // Check if any address is filled and correctly(has country)
      if (
        (!this.orderDetailService.order.invoiceAddress?.country?.length &&
          this.isAddressFilled(this.orderDetailService.order.invoiceAddress)) ||
        (!this.orderDetailService.order.shippingAddress?.country?.length &&
          this.isAddressFilled(this.orderDetailService.order.shippingAddress))
      ) {
        alert(this.translateService.instant('order-address-form.null-country.error'))
        return
      }
      if (!this.orderDetailService.isShippingAddressSameAsInvoice && !this.orderDetailService.order.shippingAddress) {
        this.orderDetailService.isShippingAddressSameAsInvoice = true
      }
      // route if ok
      this.orderDetailService.activeLink = 'payment-info'
      await this.router.navigate(['/order/payment-info'], { replaceUrl: true })
    } else if (this.orderDetailService.activeLink === 'payment-info') {
      this.orderDetailService.activeLink = ''
      await this.router.navigate(['/order'], { replaceUrl: true })
    }
  }

  isAddressFilled(address) {
    return Object.values(address).some(value => !!value)
  }

  get order() {
    return this.orderDetailService.order
  }

  set order(value) {
    this.orderDetailService.order = value
  }

  get canCreateOrEdit() {
    return this.orderDetailService.canCreateOrEdit
  }

  set canCreateOrEdit(value) {
    this.orderDetailService.canCreateOrEdit = value
  }

  private removeOrderListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['orders'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }
}
